import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    Typography,
    Grid,
    useTheme,
    IconButton,
} from '@mui/material';

import { Plus, Delete } from '../../assets/images';
import DidimHwDialog from '../../components/didimHwDialog';
import MapDialog from '../../components/mapDialog';
import OrganizationTypeEditDialog from '../../components/organizationTypeEditDialog';
import Popup from '../../components/popup';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import Dropdown from '../../components/dropdown';
import { InputField, InputFieldComponent } from '../../components/formItem';
import CommonCard from '../../components/card/CommonCard';
import { ORGANIZATION_TYPES, COUNTRY_OPTIONS, getRegionOptions, API_PAGE_SIZE } from '../../constants';
import { didimAPI, userAPI, organizationAPI, membershipAPI } from '../../actions/imsServer';
import { isAdmin } from '../../utils/base';
import { searchAddress } from '../../utils/addressUtils';
import { isEmail, isValidPassword, isZipcode } from '../../utils/password-validation';

const OrganizationUpdate = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    // 전달받은 기관 데이터
    const organizationData = location.state?.organization;

    // 폼 데이터 상태
    const [formData, setFormData] = useState({
        // 기관 정보
        organizationName: '',
        userLoginId: '',
        password: '',
        confirmPassword: '',
        type: '',
        typeLabel: '',
        divisionSections: {},
        divisionSection1: '',
        divisionSection2: '',

        // 담당자 정보
        userName: '',
        contactInfo: '',
        phone: '',
        email: '',

        // 주소 정보
        country: 'ko',
        zipcode: '',
        address: '',
        addressDetail: '',
        latitude: '',
        longitude: '',
        region: '',

        // 디딤 H/W 연결
        didimId: ''
    });

    // 기관유형 편집 팝업 상태
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    // 지도 팝업 상태
    const [mapDialogOpen, setMapDialogOpen] = useState(false);
    const [mapPosition, setMapPosition] = useState([37.5665, 126.9780]); // 기본 위치 (서울)

    // 파일 업로드 상태
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [originalFile, setOriginalFile] = useState(null); // 기존 파일 정보 저장

    // 컴포넌트 언마운트 시 URL 정리
    useEffect(() => {
        return () => {
            uploadedFiles.forEach(file => {
                if (file.previewUrl && !file.isExisting) {
                    URL.revokeObjectURL(file.previewUrl);
                }
            });
        };
    }, [uploadedFiles]);

    // 디딤H/W 선택 팝업 상태
    const [didimHwDialogOpen, setDidimHwDialogOpen] = useState(false);
    const [selectedDidimHwList, setSelectedDidimHwList] = useState([]); // 선택된 디딤H/W 목록
    const [removedDidimHwList, setRemovedDidimHwList] = useState([]); // 해제된 디딤H/W 목록

    // 계정 중복 확인 상태
    const [isCheckingLoginId, setIsCheckingLoginId] = useState(false);
    const [loginIdExists, setLoginIdExists] = useState(false);

    // 관리자 정보 상태
    const [userInfo, setUserInfo] = useState(null);

    // 삭제 확인 팝업 상태
    const [deletePopupOpen, setDeletePopupOpen] = useState(false);

    // 수정 확인 팝업 상태
    const [updatePopupOpen, setUpdatePopupOpen] = useState(false);

    // 일반 알림 팝업 상태
    const [alertPopupOpen, setAlertPopupOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSubMessage, setAlertSubMessage] = useState('');

    const [meInfo, setMeInfo] = useState(JSON.parse(localStorage.getItem('meInfo') || '{}'));

    const meInfoStorage = localStorage.getItem('meInfo');
    useEffect(() => {
        setMeInfo(JSON.parse(meInfoStorage || '{}'));
    }, [meInfoStorage]);

    // 관리자 정보 가져오기
    const fetchUserInfo = async (userId) => {
        try {
            const response = await userAPI.getUsers(userId, { state: 0 });
            const userData = response.data.user;

            if (userData) {
                setUserInfo(userData);
                // 관리자 정보로 담당자 정보 필드 업데이트
                setFormData(prev => ({
                    ...prev,
                    loginId: userData.loginId || '',
                    password: userData.loginPwd || '',
                    confirmPassword: userData.loginPwd || '',
                    userName: userData.name || '',
                }));
            }
        } catch (error) {
            console.error('관리자 정보 조회 실패:', error);
        }
    };

    // 전달받은 기관 데이터로 폼 초기화
    useEffect(() => {
        if (organizationData) {
            setFormData(prev => ({
                ...prev,
                name: organizationData.name || '',
                type: organizationData.type != null ? organizationData.type : -1,
                typeLabel: ORGANIZATION_TYPES.find(type => type.value === organizationData.type)?.label || '',
                divisionSections: organizationData.divisionSections || {},
                divisionSection1: organizationData.divisionSections ? Object.keys(organizationData.divisionSections).length.toString() : '',
                divisionSection2: organizationData.divisionSections ? Object.values(organizationData.divisionSections).map(sections => sections.length).join(',') : '',
                contactInfo: organizationData.contactInfo || '',
                phone: organizationData.phone || '',
                email: organizationData.email || '',
                country: organizationData.country || 'ko',
                zipcode: organizationData.zipcode || '',
                address: organizationData.address || '',
                addressDetail: organizationData.addressDetail || '',
                latitude: organizationData.latitude || '',
                longitude: organizationData.longitude || '',
                region: organizationData.region || '',
                didimId: organizationData.didimId || '',
                file: organizationData.file || ''
            }));

            // 관리자 정보 가져오기
            if (organizationData.userId) {
                fetchUserInfo(organizationData.userId);
            }

            // 기존 첨부서류가 있다면 업로드된 파일 목록에 추가
            if (organizationData.file) {
                // 파일 URL 구성
                let fileUrl = organizationData.file;
                // 전체 URL이 아닌 경우 baseURL 추가
                if (!fileUrl.startsWith('http')) {
                    const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
                    fileUrl = `${baseURL}${fileUrl}`;
                }

                const existingFile = {
                    name: organizationData.file.split('/').pop() || t('attachment'),
                    size: 0, // 기존 파일의 크기는 알 수 없음
                    file: null, // 기존 파일이므로 File 객체는 없음
                    isExisting: true, // 기존 파일임을 표시
                    type: organizationData.file.toLowerCase().endsWith('.pdf') ? 'application/pdf' :
                        organizationData.file.toLowerCase().endsWith('.png') ? 'image/png' :
                            organizationData.file.toLowerCase().endsWith('.jpg') || organizationData.file.toLowerCase().endsWith('.jpeg') ? 'image/jpeg' : 'image/jpeg',
                    previewUrl: fileUrl
                };
                setOriginalFile(existingFile); // 기존 파일 정보 저장
                setUploadedFiles([existingFile]);
            } else {
                setOriginalFile(null);
            }

            // 기존에 연결된 디딤 하드웨어 불러오기
            fetchConnectedDidimHw(organizationData.id);
        }
    }, [organizationData]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // 계정 중복 확인 함수
    const checkLoginIdExists = async (loginId) => {
        if (!loginId || !validateEmail(loginId)) {
            setLoginIdExists(false);
            return;
        }

        // 수정 모드에서 기존 loginId와 동일한 경우 중복 확인 생략
        if (userInfo && userInfo.loginId === loginId) {
            setLoginIdExists(false);
            return;
        }

        try {
            setIsCheckingLoginId(true);
            const response = await userAPI.checkUserExists({ loginId: loginId });
            setLoginIdExists(response.data.exists || false);
        } catch (error) {
            console.error('계정 중복 확인 실패:', error);
            setLoginIdExists(false);
        } finally {
            setIsCheckingLoginId(false);
        }
    };

    // 계정 입력 변경 핸들러 (디바운스 적용)
    const handleLoginIdChange = (value) => {
        handleInputChange('loginId', value);

        // 관리자계정이 입력되면 담당자 메일에도 자동 입력
        if (value && validateEmail(value)) {
            handleInputChange('email', value);
        }

        // 디바운스를 위해 기존 타이머 클리어
        if (window.loginIdCheckTimer) {
            clearTimeout(window.loginIdCheckTimer);
        }

        // 500ms 후에 중복 확인 실행
        window.loginIdCheckTimer = setTimeout(() => {
            checkLoginIdExists(value);
        }, 500);
    };


    // 기존에 연결된 디딤 하드웨어 불러오기 (100건씩 배치 조회)
    const fetchConnectedDidimHw = async (organizationId) => {
        try {
            let allDidims = [];
            let offset = 0;
            let page;
            do {
                const response = await didimAPI.getDidims({
                    state: '0',
                    expand: 'organization',
                    offset
                });
                page = response?.data?.didims || [];
                allDidims = allDidims.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            // 해당 기관에 연결된 디딤 하드웨어만 필터링
            const connectedDidims = allDidims
                .filter(item => item.organizationId === organizationId)
                .map(item => ({
                    id: item.id,
                    organizationName: item?.organization?.name || '-',
                    name: item.name || `${t('didim')}${item.id}`,
                    info: item.info || '',
                    uid: item.uid || '',
                    modelName: item.modelName || 'Didim',
                    organizationId: item?.organizationId || 0,
                }));

            setSelectedDidimHwList(connectedDidims);
        } catch (error) {
            console.error('연결된 디딤H/W 조회 실패:', error);
            setSelectedDidimHwList([]);
        }
    };


    // 기관유형 편집 팝업 열기
    const handleOpenEditDialog = () => {
        setEditDialogOpen(true);
    };

    // 기관유형 편집 팝업 닫기
    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
    };

    // 기관유형 편집 팝업 저장 핸들러
    const handleSaveOrganizationType = (data) => {
        const orgType = ORGANIZATION_TYPES.find(type => type.value === data.type);
        const organizationTypeLabel = orgType ? orgType.label : '';

        // divisionSection1, divisionSection2 계산
        const divisionSections = data.divisionSections || {};
        const gradeCount = Object.keys(divisionSections).length;
        const divisionSection1 = gradeCount.toString();

        // divisionSection2 계산
        let divisionSection2 = '0';
        if (gradeCount > 0 && orgType && orgType.grades && orgType.grades.length > 1) {
            const classCounts = Object.values(divisionSections).map(classes =>
                Array.isArray(classes) ? classes.length : 0
            );
            divisionSection2 = classCounts.join(',');
        }

        setFormData(prev => ({
            ...prev,
            type: data.type,
            typeLabel: organizationTypeLabel,
            divisionSection1,
            divisionSection2,
            divisionSections: divisionSections
        }));

        setEditDialogOpen(false);
    };

    // 유효성 검사 함수들
    const validateName = (name) => {
        return name && name.length >= 2 && name.length <= 20;
    };

    const validateEmail = (email) => email && isEmail(email);
    const validatePassword = (password) => password && isValidPassword(password);
    const validateZipcode = (zipcode) => zipcode && isZipcode(zipcode);

    // 전체 폼 유효성 검사
    const isFormValid = () => {
        const baseValidation = validateName(formData.name) &&
            validateZipcode(formData.zipcode) &&
            formData.type !== null && formData.type !== undefined;

        if (organizationData) {
            // 수정 모드: 관리자계정이 변경된 경우 비밀번호 필수 입력
            const isLoginIdChanged = userInfo && userInfo.loginId !== formData.loginId;

            let passwordValidation;
            if (isLoginIdChanged) {
                // 관리자계정이 변경된 경우: 비밀번호 필수 입력 및 검증
                passwordValidation = formData.password && formData.confirmPassword &&
                    validatePassword(formData.password) &&
                    formData.password === formData.confirmPassword;
            } else {
                // 관리자계정이 동일한 경우: 비밀번호가 입력된 경우에만 검증
                passwordValidation = formData.password
                    ? (validatePassword(formData.password) && formData.password === formData.confirmPassword)
                    : true; // 비밀번호가 비어있으면 검증 통과
            }

            return baseValidation && passwordValidation;
        } else {
            // 생성 모드: 모든 필드 검증
            return baseValidation &&
                validateEmail(formData.loginId) &&
                !loginIdExists && // 계정 중복이 없어야 함
                validatePassword(formData.password) &&
                formData.password === formData.confirmPassword;
        }
    };

    const handleSubmit = () => {
        if (!isFormValid()) {
            showAlert(t('please_check_your_input'));
            return;
        }
        setUpdatePopupOpen(true);
    };

    // 기관 수정 확인 핸들러
    const handleConfirmUpdate = async () => {
        try {
            setUpdatePopupOpen(false);
            let organizationId = organizationData?.id || -1;

            if (organizationData) {
                // 기관 수정 모드
                const divisionSections = formData.divisionSections || organizationData.divisionSections;

                let userId = organizationData.userId;

                // 관리자 정보도 업데이트
                if (userInfo.loginId !== formData.loginId) {
                    const userUpdateData = {
                        loginId: formData.loginId,
                        loginPwd: formData.password,
                        grade: 60,
                        state: 0,
                        name: formData.userName,
                        mobile: formData.phone,
                        email: formData.email
                    };

                    const userResponse = await userAPI.registerUser(userUpdateData);
                    userId = userResponse.data.user.id;
                } else {
                    // loginId가 동일한 경우
                    // 담당자명, 연락처, 메일이 변경된 경우 사용자 정보 업데이트
                    const isUserInfoChanged =
                        (userInfo.name !== formData.userName) ||
                        (userInfo.mobile !== formData.phone) ||
                        (userInfo.email !== formData.email);

                    if (isUserInfoChanged) {
                        await userAPI.updateUser({
                            id: userInfo.id,
                            name: formData.userName,
                            email: formData.email,
                            mobile: formData.phone,
                        });
                    }

                    // 비밀번호가 입력된 경우에만 비밀번호도 업데이트
                    if (formData.password && formData.password.trim() !== '') {
                        await userAPI.resetPassword({ loginId: userInfo.loginId, newPassword: formData.password });
                    }
                }

                // 첨부파일 처리
                const hasCurrentFile = uploadedFiles.length > 0;
                const newFile = uploadedFiles.find(file => !file.isExisting && file.file);
                const hasExistingFile = uploadedFiles.some(file => file.isExisting);

                // updateData 생성
                const updateData = {
                    id: organizationData.id,
                    userId: userId,
                    name: formData.name,
                    type: formData.type,
                    address: formData.address,
                    addressDetail: formData.addressDetail,
                    zipcode: formData.zipcode,
                    phone: formData.phone,
                    email: formData.email,
                    country: formData.country,
                    region: formData.region,
                    latitude: formData.latitude,
                    longitude: formData.longitude,
                    divisionSections: JSON.stringify(divisionSections),
                    contactInfo: formData.contactInfo,
                    file: formData.file,
                };

                // 첨부파일 처리
                if (hasCurrentFile && newFile) {
                    // 새로 업로드된 파일이 있는 경우
                    updateData.file = newFile.file; // File 객체 전달                    
                } else if (!hasCurrentFile) {
                    // 첨부파일이 없는 경우 - 기존 파일 삭제
                    updateData.file = '';
                } else if (hasCurrentFile && hasExistingFile && !newFile) {
                    // 기존 파일만 있는 경우 (새 파일 없음) - 기존 파일 유지
                    updateData.file = organizationData.file;
                }

                await organizationAPI.updateOrganization(updateData);

            } else {
                // 기관 생성 모드
                // 1. 관리자계정 생성
                const userData = {
                    loginId: formData.loginId,
                    loginPwd: formData.password,
                    name: formData.userName,
                    mobile: formData.phone,
                    email: formData.email,
                    grade: 60
                };

                const userResponse = await userAPI.registerUser(userData);
                const userId = userResponse.data.user.id;

                // 2. 기관 생성
                const divisionSections = formData.divisionSections || {};

                const newOrganizationData = {
                    userId: userId,
                    name: formData.name,
                    type: formData.type,
                    state: 0,
                    address: formData.address,
                    addressDetail: formData.addressDetail,
                    zipcode: formData.zipcode,
                    phone: formData.phone,
                    email: formData.email,
                    country: formData.country,
                    region: formData.region,
                    latitude: formData.latitude,
                    longitude: formData.longitude,
                    divisionSections: JSON.stringify(divisionSections),
                    contactInfo: formData.contactInfo,
                    file: formData.file,
                };

                // 새로 업로드된 파일이 있는 경우 전달
                const uploadedFile = uploadedFiles.find(file => !file.isExisting && file.file);
                if (uploadedFile) {
                    newOrganizationData.file = uploadedFile.file;
                } else {
                    newOrganizationData.file = '';
                }

                const organizationResponse = await organizationAPI.registerOrganization(newOrganizationData);
                organizationId = organizationResponse.data.organization.id;

                await membershipAPI.registerMembership({
                    userId: userId,
                    organizationId: organizationId,
                    type: 0,
                    state: 0,
                });

                showAlert(t('your_application_has_been_received_we_will_notify_you_of_the_approval_or_rejection_status_as_soon_as_possible'));

            }

            // 3. 디딤 하드웨어 연결 관리
            // 3-1. 새로 선택된 디딤 하드웨어 연결
            if (selectedDidimHwList.length > 0) {
                console.log('디딤 기관 등록 중...');
                for (const didimHw of selectedDidimHwList) {
                    try {
                        await didimAPI.updateDidim({
                            id: didimHw.id,
                            organizationId: organizationId
                        });
                        console.log(`디딤 ${didimHw.id} 기관 등록 완료`);
                    } catch (error) {
                        console.error(`디딤 ${didimHw.id} 기관 등록 실패:`, error);
                    }
                }
            }

            // 3-2. 해제된 디딤 하드웨어 연결 해제
            if (removedDidimHwList.length > 0) {
                console.log('디딤 기관 연결 해제 중...');
                for (const didimHw of removedDidimHwList) {
                    try {
                        await didimAPI.updateDidim({
                            id: didimHw.id,
                            organizationId: 0 // 0으로 설정하여 연결 해제
                        });
                        console.log(`디딤 ${didimHw.id} 기관 연결 해제 완료`);
                    } catch (error) {
                        console.error(`디딤 ${didimHw.id} 기관 연결 해제 실패:`, error);
                    }
                }
            }

            showAlert(t('organization_has_been_processed_successfully'));

            // 기관 수정 모드인 경우 detail 페이지로 이동하면서 업데이트된 데이터 전달
            if (organizationData) {
                try {
                    // 업데이트된 기관 정보 다시 가져오기
                    const updatedOrgResponse = await organizationAPI.getOrganizations({
                        id: organizationId
                    });
                    const updatedOrganization = updatedOrgResponse?.data?.organizations?.[0] ||
                        updatedOrgResponse?.organizations?.[0] || null;

                    if (updatedOrganization) {
                        navigate('/organizations/detail', {
                            state: {
                                organization: updatedOrganization,
                                shouldReload: true
                            }
                        });
                    } else {
                        navigate(-1);
                    }
                } catch (err) {
                    console.error('업데이트된 기관 정보 가져오기 실패:', err);
                    navigate(-1);
                }
            } else {
                navigate(-1);
            }

        } catch (error) {
            console.error('기관 처리 실패:', error);
            showAlert(t('an_error_occurred_while_processing_please_try_again'));
        }
    };

    // 기관 수정 취소 핸들러
    const handleCancelUpdate = () => {
        setUpdatePopupOpen(false);
    };

    const handleCancel = () => {
        navigate(-1);
    };

    // 주소 검색 핸들러 (우편번호 또는 주소 일부로 검색 가능)
    const handleAddressSearch = async (searchQuery) => {
        await searchAddress(searchQuery, formData.country, (addressData) => {
            setFormData(prev => ({
                ...prev,
                zipcode: addressData.zipcode || prev.zipcode,
                address: addressData.address,
                latitude: addressData.latitude,
                longitude: addressData.longitude,
                region: addressData.region
            }));
        });
    };

    // 우편번호 검색 핸들러 (기존 호환성을 위해 유지)
    const handleZipcodeSearch = async (zipcode) => {
        await handleAddressSearch(zipcode);
    };

    // 지도 팝업 핸들러
    const handleOpenMapDialog = () => {
        // 현재 좌표가 있으면 해당 위치로 설정, 없으면 기본 위치
        const currentLat = parseFloat(formData.latitude) || 37.5665;
        const currentLng = parseFloat(formData.longitude) || 126.9780;
        setMapPosition([currentLat, currentLng]);
        setMapDialogOpen(true);
    };

    const handleCloseMapDialog = () => {
        setMapDialogOpen(false);
    };

    const handleMapPositionSave = (position) => {
        setFormData(prev => ({
            ...prev,
            latitude: position[0].toString(),
            longitude: position[1].toString()
        }));
        setMapDialogOpen(false);
    };

    // 디딤H/W 선택 팝업 핸들러
    const handleOpenDidimHwDialog = () => {
        setDidimHwDialogOpen(true);
    };

    const handleCloseDidimHwDialog = () => {
        setDidimHwDialogOpen(false);
    };

    const handleSelectDidimHw = (didimHw) => {
        // 이미 선택된 디딤H/W인지 확인
        const isAlreadySelected = selectedDidimHwList.some(item => item.id === didimHw.id);

        if (!isAlreadySelected) {
            setSelectedDidimHwList(prev => [...prev, didimHw]);
        }

        setDidimHwDialogOpen(false);
    };

    // 디딤H/W 제거 핸들러
    const handleRemoveDidimHw = (didimHwId) => {
        // 제거할 디딤 하드웨어 정보 찾기
        const removedItem = selectedDidimHwList.find(item => item.id === didimHwId);

        if (removedItem) {
            // 해제된 목록에 추가
            setRemovedDidimHwList(prev => [...prev, removedItem]);
        }

        // 선택된 목록에서 제거
        setSelectedDidimHwList(prev => prev.filter(item => item.id !== didimHwId));
    };





    // 파일 업로드 핸들러
    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);

        // 최대 1개 파일 제한
        if (files.length > 1) {
            showAlert(t('maximum_1_attachment_can_be_uploaded'));
            return;
        }

        const validFiles = files.filter(file => {
            // 파일 크기 제한 (10MB)
            if (file.size > 10 * 1024 * 1024) {
                showAlert(t('file_size_cannot_exceed_10mb'));
                return false;
            }
            // 파일 형식 제한 (PDF, 이미지 파일)
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                showAlert(t('only_pdf_jpeg_png_files_are_allowed'));
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) return;

        // 파일에 미리보기 URL 추가
        const newFile = validFiles[0];
        const previewUrl = URL.createObjectURL(newFile);

        const fileWithPreview = {
            name: newFile.name,
            size: newFile.size,
            type: newFile.type,
            file: newFile,
            previewUrl: previewUrl,
            isExisting: false
        };

        // 새 파일로 교체 (기존 파일 정보는 originalFile에 보관)
        setUploadedFiles([fileWithPreview]);

        // 파일명을 폼 데이터에 업데이트
        setFormData(prev => ({
            ...prev,
            file: newFile.name
        }));
    };

    // 드래그 앤 드롭 핸들러
    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const files = Array.from(event.dataTransfer.files);

        // 최대 1개 파일 제한
        if (files.length > 1) {
            showAlert(t('maximum_1_attachment_can_be_uploaded'));
            return;
        }

        const validFiles = files.filter(file => {
            // 파일 크기 제한 (10MB)
            if (file.size > 10 * 1024 * 1024) {
                showAlert(t('file_size_cannot_exceed_10mb'));
                return false;
            }
            // 파일 형식 제한 (PDF, 이미지 파일)
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
            if (!allowedTypes.includes(file.type)) {
                showAlert(t('only_pdf_jpeg_png_files_are_allowed'));
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) return;

        // 파일에 미리보기 URL 추가
        const newFile = validFiles[0];
        const previewUrl = URL.createObjectURL(newFile);

        const fileWithPreview = {
            name: newFile.name,
            size: newFile.size,
            type: newFile.type,
            file: newFile,
            previewUrl: previewUrl,
            isExisting: false
        };

        // 새 파일로 교체
        setUploadedFiles([fileWithPreview]);

        // 파일명을 폼 데이터에 업데이트
        setFormData(prev => ({
            ...prev,
            file: newFile.name
        }));
    };

    // 기관 삭제 핸들러
    const handleDeleteOrganization = () => {
        if (!organizationData) {
            showAlert(t('no_organization_to_delete'));
            return;
        }
        setDeletePopupOpen(true);
    };

    // 기관 삭제 확인 핸들러
    const handleConfirmDelete = async () => {
        try {
            setDeletePopupOpen(false);

            await organizationAPI.unregisterOrganization({
                id: organizationData.id
            });

            showAlert(t('organization_has_been_deleted'));
            navigate('/organizations/list');
        } catch (error) {
            console.error('기관 삭제 실패:', error);
            setDeletePopupOpen(false);
            showAlert(t('failed_to_delete_organization_please_try_again'));
        }
    };

    // 기관 삭제 취소 핸들러
    const handleCancelDelete = () => {
        setDeletePopupOpen(false);
    };

    // 알림 팝업 표시 함수
    const showAlert = (message, subMessage = '') => {
        setAlertMessage(message);
        setAlertSubMessage(subMessage);
        setAlertPopupOpen(true);
    };

    // 알림 팝업 닫기 함수
    const handleCloseAlert = () => {
        setAlertPopupOpen(false);
    };

    return (
        <ContentArea padding='20px 10px 10px 10px'>
            {/* 헤더 영역 */}
            {organizationData && !isAdmin(meInfo?.grade) && (
                <Button
                    onClick={handleDeleteOrganization}
                    sx={{
                        marginRight: '10px',
                        alignSelf: 'flex-end',
                    }}
                >
                    {t('delete_organization')}
                </Button>
            )}

            {/* 폼 영역 */}
            <Box sx={{ px: 3, pb: 4, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Grid container spacing={4} sx={{ flex: 1 }}>
                    {/* 왼쪽 컬럼 - 기관 정보 및 담당자 정보 */}
                    <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* 기관 정보 섹션 */}
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3, mb: 4 }}>
                            <InputFieldComponent title={`${t('organization_name')} *`} >
                                <InputField
                                    placeholder={t('organization_name_2_20_characters')}
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    error={formData.name && !validateName(formData.name)}
                                    helperText={formData.name && !validateName(formData.name) ? t('organization_name_must_be_2_to_20_characters') : ' '}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={`${t('organization_admin_account')} *`} >
                                <InputField
                                    placeholder={t('email_format')}
                                    value={formData.loginId}
                                    onChange={(e) => handleLoginIdChange(e.target.value)}
                                    error={!organizationData && formData.loginId && (!validateEmail(formData.loginId) || loginIdExists)}
                                    helperText={
                                        (formData.loginId && !validateEmail(formData.loginId)
                                            ? t('please_enter_a_valid_email_address')
                                            : formData.loginId && loginIdExists
                                                ? t('this_account_is_already_in_use')
                                                : formData.loginId && isCheckingLoginId
                                                    ? t('checking_account_availability')
                                                    : formData.loginId && !loginIdExists && validateEmail(formData.loginId)
                                                        ? t('account_is_available')
                                                        : ' ')
                                    }
                                    disabled={isAdmin(meInfo?.grade)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('account_password')} >
                                <InputField
                                    type="password"
                                    showVisibilityIcon={true}
                                    placeholder={
                                        organizationData
                                            ? (userInfo && userInfo.loginId !== formData.loginId
                                                ? t('enter_new_admin_account_password')
                                                : t('enter_new_password_to_change'))
                                            : t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters')
                                    }
                                    value={formData.password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    error={
                                        // 양쪽 모두 입력되어 있고 불일치하면 즉시 에러
                                        (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) ||
                                        (
                                            organizationData
                                                ? (userInfo && userInfo.loginId !== formData.loginId
                                                    ? (!formData.password || !validatePassword(formData.password))
                                                    : (formData.password && !validatePassword(formData.password)))
                                                : (formData.password && !validatePassword(formData.password))
                                        )
                                    }
                                    helperText={
                                        (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword)
                                            ? t('password_mismatch')
                                            : (
                                                organizationData
                                                    ? (userInfo && userInfo.loginId !== formData.loginId
                                                        ? (formData.password && !validatePassword(formData.password)
                                                            ? t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters')
                                                            : t('enter_new_admin_account_password'))
                                                        : (formData.password
                                                            ? (formData.password && !validatePassword(formData.password)
                                                                ? t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters')
                                                                : t('enter_new_password_to_change'))
                                                            : t('leave_blank_if_you_do_not_want_to_change_password')))
                                                    : (formData.password && !validatePassword(formData.password) ? t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters') : ' ')
                                            )
                                    }
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('account_password_confirmation')} >
                                <InputField
                                    type="password"
                                    showVisibilityIcon={true}
                                    placeholder={
                                        organizationData
                                            ? (userInfo && userInfo.loginId !== formData.loginId
                                                ? t('re_enter_new_admin_account_password')
                                                : t('re_enter_new_password'))
                                            : t('confirm_your_password')
                                    }
                                    value={formData.confirmPassword}
                                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                    error={
                                        (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword) ||
                                        (
                                            organizationData
                                                ? (userInfo && userInfo.loginId !== formData.loginId
                                                    ? (!formData.confirmPassword || formData.password !== formData.confirmPassword)
                                                    : (formData.confirmPassword && formData.password !== formData.confirmPassword))
                                                : (formData.confirmPassword && formData.password !== formData.confirmPassword)
                                        )
                                    }
                                    helperText={
                                        (formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword)
                                            ? t('password_mismatch')
                                            : (
                                                organizationData
                                                    ? (userInfo && userInfo.loginId !== formData.loginId
                                                        ? (formData.password && formData.password !== formData.confirmPassword
                                                            ? t('password_mismatch')
                                                            : t('re_enter_new_admin_account_password'))
                                                        : (formData.password && formData.password !== formData.confirmPassword
                                                            ? t('password_mismatch')
                                                            : ' '))
                                                    : (formData.confirmPassword && formData.password !== formData.confirmPassword ? t('password_mismatch') : ' ')
                                            )
                                    }
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('organization_type') + ' *'} >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <InputField
                                        placeholder={t('please_select')}
                                        disabled
                                        value={formData.typeLabel ? t(formData.typeLabel) : formData.type}
                                        onChange={(e) => handleInputChange('type', e.target.value)}
                                        showClearIcon={false}
                                    />
                                    <Button size="small" onClick={handleOpenEditDialog}>
                                        {t('edit')}
                                    </Button>
                                </Box>
                            </InputFieldComponent>

                            <InputFieldComponent title={t('division_1')} >
                                <InputField
                                    placeholder={t('auto_filled_when_organization_type_is_edited')}
                                    disabled
                                    showClearIcon={false}
                                    value={formData.divisionSection1}
                                    onChange={(e) => handleInputChange('divisionSection1', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('division_2')} >
                                <InputField
                                    placeholder={t('auto_filled_when_organization_type_is_edited')}
                                    disabled
                                    showClearIcon={false}
                                    value={formData.divisionSection2}
                                    onChange={(e) => handleInputChange('divisionSection2', e.target.value)}
                                />
                            </InputFieldComponent>
                        </CommonCard>

                        {/* 담당자 정보 섹션 */}
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                            <InputFieldComponent title={t('contact_name')} >
                                <InputField
                                    placeholder={t('enter_your_name')}
                                    value={formData.userName}
                                    onChange={(e) => handleInputChange('userName', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('contact_title')} >
                                <InputField
                                    placeholder={t('enter_contact_title')}
                                    value={formData.contactInfo}
                                    onChange={(e) => handleInputChange('contactInfo', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('contact_phone')} >
                                <InputField
                                    placeholder={t('enter_contact_phone')}
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                />
                            </InputFieldComponent>


                            <InputFieldComponent title={t('contact_email')} >
                                <InputField
                                    placeholder={t('please_enter_notification_email')}
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                />
                            </InputFieldComponent>


                            {(!uploadedFiles.length || uploadedFiles.some(file => file.isExisting)) && (<InputFieldComponent title={t('attachment')} sx={{ height: '128px' }}>
                                {/* 파일 업로드 영역 - 첨부서류가 없거나 기존 파일이 있는 경우에만 표시 */}

                                <Box sx={{
                                    border: `2px dashed ${theme.palette.figma.white[20]}`,
                                    borderRadius: '8px',
                                    p: 3,
                                    textAlign: 'center',
                                    backgroundColor: theme.palette.figma.white[5],
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: theme.palette.figma.white[10],
                                        borderColor: theme.palette.figma.white[30]
                                    }
                                }}
                                    onDragOver={handleDragOver}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}>
                                    <input
                                        type="file"
                                        multiple
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={handleFileUpload}
                                        style={{ display: 'none' }}
                                        id="file-upload"
                                    />
                                    <label htmlFor="file-upload" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                                        <Typography sx={{
                                            color: theme.palette.figma.white[95],
                                            fontSize: '14px',
                                            mb: 1
                                        }}>
                                            {t('business_registration_or_employment_certificate')}
                                        </Typography>
                                        <Typography sx={{
                                            color: theme.palette.figma.white[60],
                                            fontSize: '14px',
                                            mb: 1
                                        }}>
                                            {uploadedFiles.some(file => file.isExisting)
                                                ? t('drag_or_click_to_replace_with_new_file')
                                                : t('drag_or_click_to_upload_file')
                                            }
                                        </Typography>
                                        <Typography sx={{
                                            color: theme.palette.figma.white[40],
                                            fontSize: '12px'
                                        }}>
                                            {t('pdf_jpeg_png_only_max_1_file_10mb')}
                                        </Typography>
                                    </label>
                                </Box>

                            </InputFieldComponent>)}
                            {/* 업로드된 파일 목록 */}
                            {uploadedFiles.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    {uploadedFiles.map((file, index) => (
                                        <Box key={index} sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            p: 2,
                                            backgroundColor: theme.palette.figma.white[5],
                                            borderRadius: '6px',
                                            border: `1px solid ${theme.palette.figma.white[20]}`
                                        }}>
                                            {/* 미리보기 이미지 */}
                                            {file.type && file.type.startsWith('image/') && file.previewUrl && (
                                                <Box sx={{
                                                    width: '60px',
                                                    height: '60px',
                                                    borderRadius: '6px',
                                                    overflow: 'hidden',
                                                    border: `1px solid ${theme.palette.figma.white[20]}`,
                                                    flexShrink: 0
                                                }}>
                                                    <img
                                                        src={file.previewUrl}
                                                        alt={file.name}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                            {/* PDF 파일 아이콘 */}
                                            {file.type === 'application/pdf' && (
                                                <Box sx={{
                                                    width: '60px',
                                                    height: '60px',
                                                    borderRadius: '6px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: theme.palette.figma.white[10],
                                                    border: `1px solid ${theme.palette.figma.white[20]}`,
                                                    flexShrink: 0
                                                }}>
                                                    <Typography sx={{
                                                        fontSize: '20px',
                                                        color: theme.palette.error.main,
                                                        fontWeight: 700
                                                    }}>
                                                        PDF
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{
                                                    fontSize: '12px',
                                                    color: theme.palette.figma.white[80],
                                                    fontWeight: 500,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {file.name}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: '11px',
                                                    color: theme.palette.figma.white[60],
                                                    mt: 0.5
                                                }}>
                                                    {file.isExisting ? t('existing_attachment') : `${(file.size / 1024).toFixed(0)}KB`}
                                                </Typography>
                                            </Box>
                                            {/* 삭제 버튼 - 새 파일만 표시 */}
                                            {!file.isExisting && (
                                                <IconButton
                                                    onClick={() => {
                                                        // URL 정리
                                                        if (file.previewUrl) {
                                                            URL.revokeObjectURL(file.previewUrl);
                                                        }

                                                        // 기존 파일이 있으면 복원, 없으면 빈 상태로
                                                        if (originalFile) {
                                                            setUploadedFiles([originalFile]);
                                                            setFormData(prev => ({ ...prev, file: organizationData.file }));
                                                        } else {
                                                            setUploadedFiles([]);
                                                            setFormData(prev => ({ ...prev, file: '' }));
                                                        }
                                                    }}
                                                    size="small"
                                                >
                                                    <Delete
                                                        size={20}
                                                        color={theme.palette.figma.icon}
                                                    />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </CommonCard>

                    </Grid>

                    {/* 오른쪽 컬럼 - 주소 정보 및 디딤 H/W 연결 */}
                    <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* 주소 정보 섹션 */}
                        <CommonCard sx={{ alignItems: 'stretch', gap: 3, mb: 4 }}>
                            <InputFieldComponent title={t('country') + ' *'} >
                                <Dropdown
                                    options={COUNTRY_OPTIONS}
                                    value={formData.country}
                                    onChange={(option) => handleInputChange('country', option.value)}
                                    placeholder={t('select_country')}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('address') + ' *'} >
                                <InputField
                                    placeholder={formData.country === 'ko' ? t('enter_part_of_address_e_g_seoul_sejong_daero_110') : t('will_be_auto_filled_when_zipcode_is_entered')}
                                    value={formData.address}
                                    onChange={(e) => handleInputChange('address', e.target.value)}
                                    onBlur={(e) => {
                                        // 대한민국이고 주소가 입력되었지만 우편번호가 없는 경우 주소 검색
                                        if (formData.country === 'ko' && e.target.value && e.target.value.trim().length >= 2) {
                                            handleAddressSearch(e.target.value);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        // 엔터키 입력 시 주소 검색
                                        if (e.key === 'Enter' && formData.country === 'ko' && formData.address && formData.address.trim().length >= 2) {
                                            e.preventDefault();
                                            handleAddressSearch(formData.address);
                                        }
                                    }}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('address_detail')} >
                                <InputField
                                    placeholder={t('enter_address_detail')}
                                    value={formData.addressDetail}
                                    onChange={(e) => handleInputChange('addressDetail', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('zip_code') + ' *'} >
                                <InputField
                                    placeholder={t('enter_zip_code') + ' (5-6)'}
                                    value={formData.zipcode}
                                    onChange={(e) => handleInputChange('zipcode', e.target.value)}
                                    onBlur={(e) => handleZipcodeSearch(e.target.value)}
                                    error={formData.zipcode && !validateZipcode(formData.zipcode)}
                                    helperText={formData.zipcode && !validateZipcode(formData.zipcode) ? t('please_enter_a_valid_zip_code') : ' '}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('latitude') + ' *'} >
                                <InputField
                                    placeholder={t('auto_filled_from_zip_code')}
                                    value={formData.latitude}
                                    onChange={(e) => handleInputChange('latitude', e.target.value)}
                                />
                            </InputFieldComponent>

                            <InputFieldComponent title={t('longitude') + ' *'} >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <InputField
                                        placeholder={t('auto_filled_from_zip_code')}
                                        value={formData.longitude}
                                        onChange={(e) => handleInputChange('longitude', e.target.value)}
                                    />
                                    <Button size="small" onClick={handleOpenMapDialog}>
                                        {t('edit_details')}
                                    </Button>
                                </Box>
                            </InputFieldComponent>

                            <InputFieldComponent title={t('region') + ' *'} >
                                <Dropdown
                                    options={getRegionOptions(formData.country)}
                                    value={formData.region}
                                    onChange={(option) => handleInputChange('region', option.value)}
                                    placeholder={t('please_select_region')}
                                />
                            </InputFieldComponent>
                        </CommonCard>

                        {!isAdmin(meInfo?.grade) && (
                            <>
                                {/* 디딤 H/W 연결 섹션 */}
                                <Typography sx={{
                                    mb: 2,
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    letterSpacing: '-0.42px',
                                    textAlign: 'left'
                                }}>
                                    {t('didim_h_w_connection')}
                                </Typography>
                                <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                                    <Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {/* 선택된 디딤H/W 목록 표시 */}
                                            {selectedDidimHwList.map((item, index) => (
                                                <Box key={item.id} sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr auto',
                                                    alignItems: 'center',
                                                    backgroundColor: theme.palette.figma.white[5],
                                                    borderRadius: '8px',
                                                    padding: '8px 12px',
                                                    border: `1px solid ${theme.palette.figma.white[20]}`
                                                }}>
                                                    <Typography sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                                                        textAlign: 'left'
                                                    }}>
                                                        {t('didim_id')} {item.id}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveDidimHw(item.id)}
                                                        sx={{
                                                            padding: '4px',
                                                            color: theme.palette.figma.white[60],
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.figma.white[10],
                                                                color: theme.palette.error.main
                                                            }
                                                        }}
                                                    >
                                                        <Delete
                                                            size={20}
                                                            color={theme.palette.figma.icon}
                                                        />
                                                    </IconButton>
                                                </Box>
                                            ))}

                                            {/* 해제된 디딤H/W 목록 표시 */}
                                            {removedDidimHwList.map((item, index) => (
                                                <Box key={`removed-${item.id}`} sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr auto',
                                                    alignItems: 'center',
                                                    backgroundColor: theme.palette.figma.white[5],
                                                    borderRadius: '8px',
                                                    padding: '8px 12px',
                                                    border: `1px solid ${theme.palette.error.main}`,
                                                    opacity: 0.7
                                                }}>
                                                    <Typography sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        color: theme.palette.error.main,
                                                        textAlign: 'left'
                                                    }}>
                                                        {t('didim_id')} {item.id} ({t('to_be_removed')})
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            // 해제된 목록에서 제거하고 다시 선택된 목록에 추가
                                                            setRemovedDidimHwList(prev => prev.filter(removedItem => removedItem.id !== item.id));
                                                            setSelectedDidimHwList(prev => [...prev, item]);
                                                        }}
                                                        sx={{
                                                            padding: '4px',
                                                            color: theme.palette.error.main,
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.figma.white[10],
                                                                color: theme.palette.success.main
                                                            }
                                                        }}
                                                    >
                                                        <Plus size={16} />
                                                    </IconButton>
                                                </Box>
                                            ))}

                                            {/* 추가 버튼 */}
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: theme.palette.figma.white[5],
                                                borderRadius: '8px',
                                                padding: '8px 12px',
                                                border: `1px solid ${theme.palette.figma.white[20]}`,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.figma.white[10]
                                                }
                                            }}
                                                onClick={handleOpenDidimHwDialog}
                                            >
                                                <Plus size={20} color={theme.palette.figma.white[60]} />
                                            </Box>
                                        </Box>
                                    </Box>
                                </CommonCard>
                            </>
                        )}
                        {/* 하단 버튼 영역 */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-end',
                            gap: 2,
                            px: 2.5,
                            pb: 3,
                            alignSelf: 'stretch',
                            marginTop: 'auto'
                        }}>
                            <Button
                                variant="gray"
                                onClick={handleCancel}
                                sx={{ minWidth: '120px' }}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={!isFormValid()}
                                sx={{ minWidth: '160px' }}
                            >
                                {organizationData ? t('edit_organization') : t('submit_organization_application')}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* 기관유형 편집 팝업 */}
            <OrganizationTypeEditDialog
                open={editDialogOpen}
                onClose={handleCloseEditDialog}
                onSave={handleSaveOrganizationType}
                initialType={formData.type}
                initialDivisionSections={formData.divisionSections}
                disabled={isAdmin(meInfo?.grade)}
            />

            {/* 지도 팝업 */}
            <MapDialog
                open={mapDialogOpen}
                onClose={handleCloseMapDialog}
                initialPosition={mapPosition}
                onSave={handleMapPositionSave}
            />

            {/* 디딤H/W 선택 팝업 */}
            <DidimHwDialog
                open={didimHwDialogOpen}
                onClose={handleCloseDidimHwDialog}
                onSelect={handleSelectDidimHw}
                excludeIds={selectedDidimHwList.map(d => d.id)}
            />

            {/* 기관 삭제 확인 팝업 */}
            <Popup
                open={deletePopupOpen}
                message={t('delete_organization_name', { name: organizationData?.name || '' })}
                subMessage={t('this_action_cannot_be_undone')}
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
            />

            {/* 기관 수정 확인 팝업 */}
            <Popup
                open={updatePopupOpen}
                message={organizationData ? t('confirm_update_organization', { name: organizationData.name || '' }) : t('register_organization')}
                subMessage={organizationData ? t('your_changes_will_be_saved') : t('registered_organization_will_be_activated_after_admin_approval')}
                onCancel={handleCancelUpdate}
                onConfirm={handleConfirmUpdate}
            />

            {/* 일반 알림 팝업 */}
            <Popup
                open={alertPopupOpen}
                message={alertMessage}
                subMessage={alertSubMessage}
                onConfirm={handleCloseAlert}
            />

        </ContentArea >
    );
};

export default OrganizationUpdate;
